import { from, of, ReplaySubject, tap } from 'rxjs';
import { first, map, share, switchMap } from 'rxjs/operators';

import { defaultLang, lang$, readJson } from './http.js';

export let promptTemplates$ = lang$.pipe(
    switchMap(lang => {
        if (lang === defaultLang) {
            return of({});
        }
        return fetchPromptTemplates(lang).pipe(
            share({
                connector: () => new ReplaySubject(1),
                resetOnRefCountZero: false,
                resetOnComplete: false,
                resetOnError: true,
            }),
        );
    }),
    share({
        connector: () => new ReplaySubject(1),
        resetOnRefCountZero: false,
        resetOnComplete: false,
        resetOnError: true,
    }),
);

function fetchPromptTemplates(lang) {
    return of(undefined).pipe(
        switchMap(() => from(fetch(`/prompt-templates-${lang}.json`))),
        readJson(),
    );
}

let defaultPrompts = {
    'end-of': '(end of {{section}})',
    'near': '{{place}} near {{near}}',
    'empty-set': 'EMPTY-SET',
    'extract-query-info': '# Task\n\nAnswer the questionnaire exclusively with information from the provided text. Ignore questions that cannot be answered. Respond with the string `EMPTY-SET` if you cannot answer any of the questions in the questionnaire. The answers must be formulated in English.\n\n## Questionnaire\n\n{{query}}\n## Source Text\n\n```markdown\n{{content}}\n```\n\n## Answers\n\n',
    'here&now': 'You are assisting {{given_name}}. {{given_name}}\'s full name is {{given_name}} {{family_name}}. The current date and time are {{now}}.',
    'location': 'The current location is in the region {{location}}.',
    'build-title': 'Create a summarizing title using the information from the previous {{section}}. The title must be plain text without markdown formatting.',
    'answer-with-knowledge': '# Task\n\nRespond the request with additional knowledge from the context.\nAlways include citations when referencing external knowledge. You must include citations immediately after the relevant text as a markdown link to the external URL. The link text of the markdown link always uses the prefix \'§ \' followed by the domain name of the external URL in braces. For example: {{exampleCitation}}\nThe string `EMPTY-SET` within the section \'Context\' means that no information is available for that item.\n\n# Context\n\n{{knowledge}}\n\n# Request\n\n{{user}}\n\n# Response\n',
    'categorize': '# Task\n\nAnalyze the user request and respond with a JSON object containing the following properties:\n\n- `conversationLanguageName` is the name of the language used in the conversation.\n- `internetResearchSearchQueries` are web search engine query strings to retrieve complementary information. Only populate `internetResearchSearchQueries` if an internet search would genuinely benefit the conversation. The queries within `internetResearchSearchQueries` should be a thematically distinct set, limiting them to the minimum necessary.\n- `scientificPaperSearchTerms` is a list of queries to search a database of scientific papers. Only provide `scientificPaperSearchTerms` if the conversation would benefit from accessing abstracts.\n- `topics` is a list of topics and people present in the conversation.\n- `summarizingQuestion` needs to contain a question that can extract useful information for the conversation.\n- `dataCollectionQuestions` must contain up to {{maxDataCollectionQuestions}} questions that collect relevant information for the previous conversation. Also, research background information on people mentioned in the conversation and determine their preferred communication style or writing format for audience engagement.\n- `productSearch` specifies if the request is a product search on the internet or in online shops.\n\n# User request\n\n{{userRequest}}\n\n# JSON Response\n\n',
    'start-product-hunt': '# Task\n\nAnalyze the product search query from the "Request" section and respond with a JSON object.\n\nThe JSON field `productName` should briefly describe the product being searched for.\n\nThe JSON array `acceptanceCriteria` should list the positive and negative criteria mentioned in the request for the product. The criteria must require specific product characteristics and not be general in nature.\n\n## Request\n\n{{lastUserRequest}}\n\n## Example JSON Response\n\n{\n  "productName": "Blue Umbrella",\n  "acceptanceCriteria": [\n    "The umbrella must be blue.",\n    "The handle must not be made of plastic."\n  ]\n}\n\n## JSON Response',
    'update-product-hunt': '# Task\n\nGiven the current product search query as JSON and a user request, apply the task from the user request to the JSON product search query and respond with a new, adapted JSON product search query.\n\nThe JSON field `productName` should briefly describe the product being searched for.\n\nThe JSON array `acceptanceCriteria` should list the positive and negative criteria mentioned in the request for the product. The criteria must specify particular product features and not be general.\n\n## User Request\n\n{{lastUserMessage}}\n\n## JSON Product Search Query\n\n```json\n{{currentProductHuntCriteria}}\n```\n\n## Updated JSON Response',
    'task': 'Task',
    'task-placeholder': 'Task…',
    'section-input': 'Input',
    'section-headline-placeholder': 'Input…',
    'section-content-placeholder': 'Content…',
    'response': 'Response',
    'task-response-placeholder': 'Response…',
    'add-section': '+ add section',
    'loading': 'Loading…',
    'submit': 'send',
};

export function getPrompts() {
    return promptTemplates$.pipe(
        map(templates => {
            let prompts = [];
            for (let name of Object.keys(templates)) {
                let template = templates[name];
                prompts.push({
                    name,
                    template,
                    variables: listTemplateVariables(template),
                });
            }
            return prompts;
        }),
    );
}

function listTemplateVariables(template) {
    let variables = [];
    let cur = 0;
    for (;;) {
        cur = template.indexOf('{{', cur);
        if (cur === -1) {
            break;
        }
        let end = template.indexOf('}}', cur);
        if (end === -1) {
            break;
        }
        variables.push(template.substring(cur + 2, end));
        cur = end + 2;
    }
    return variables;
}

export function buildPrompt(name, vars) {
    return promptTemplates$.pipe(
        first(),
        switchMap(templates => {
            if (templates.hasOwnProperty(name)) {
                return of(templates[name]);
            }
            if (defaultPrompts.hasOwnProperty(name)) {
                return of(defaultPrompts[name]);
            }
            throw new Error(`No prompt template found for name ${name}`);
        }),
        map(template => formatTemplate(template, vars)),
    );
}

export function buildCitation(url) {
    let u;
    try {
        u = new URL(url);
    } catch (e) {
        console.warn('Unable to build well formatted citation for URL:', url);
        return `[§ ${url}](${url})`;
    }
    let host = u.hostname;
    let wwwPrefix = 'www.';
    if (host.startsWith(wwwPrefix)) {
        host = host.substring(wwwPrefix.length);
    }
    return `[§ ${host}](${url})`;
}

export function formatTemplate(template, vars) {
    let result = '';
    let i = 0;
    while (i < template.length) {
        if (template.startsWith('{{', i)) {
            let j = i + 2;
            while (j < template.length && template[j] !== '}') {
                j++;
            }
            if (j < template.length && template[j+1] === '}') {
                let variableName = template.substring(i + 2, j);
                let variableValue = vars[variableName];
                if (variableValue === undefined) {
                    throw new Error(`Unknown variable ${variableName} used in template string: ${template}`);
                }
                result += variableValue;
                i = j + 2; // }}
            } else {
                throw new Error(`Unterminated variable found in template string: ${template}`);
            }
        } else {
            result += template[i];
            i++;
        }
    }
    return result;
}

export function buildEndOfPrompt(section) {
    return buildPrompt('end-of', {
        section,
    });
}

let translateables = [
    ['', (el, trans) => el.textContent = trans],
    ['-placeholder', (el, trans) => el.setAttribute('placeholder', trans)],
];

export function translateDom(root) {
    return promptTemplates$.pipe(
        tap(templates => {
            let selector = translateables.map(([suffix, applicator]) => `*[data-translate${suffix}]`).join(', ');
            for (let element of root.querySelectorAll(selector)) {
                for (let [suffux, applicator] of translateables) {
                    let key = element.getAttribute(`data-translate${suffux}`);
                    if (!key) {
                        continue;
                    }
                    let trans = templates[key];
                    if (!trans) {
                        console.warn(`Unable to translate ${key} on`, element);
                        continue;
                    }
                    applicator(element, trans);
                }
            }
        }),
    );
}
